import type { Automation, AutomationInput, RunSummary, ServerEvent } from '@harness/shared/protocol';
import { useEffect, useState } from 'react';
import { type AutomationResult, automationAction, deleteAutomation, fetchAllRuns, fetchRun, fetchRunEvents, fetchRuns, runAutomationNow, saveAutomation } from '../api';
import { type AutomationRoute, automationUrl } from '../sections';
import { initialState, reduce } from '../state';
import appStyles from '../styles/app.module.css';
import styles from '../styles/automations.module.css';
import { approvalTitle, pauseText, runStatusText, runTriggerText, scheduleText, sectionHeader, tabText, TEXT } from '../texts';
import { AppLink } from './AppLink';
import { Confirm, Editor } from './AutomationDialogs';
import { PageHeader } from './PageHeader';
import { Timeline } from './Timeline';
import { WeekPlan } from './WeekPlan';

// Month as a name and 24-hour time, unambiguous for every reader.
const WHEN = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const COST = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** How often the page of a running run is reloaded. */
const RUN_POLL_MS = 3000;

interface AutomationsProps {
  projectId: string;
  /** Prompts by this name are labelled "You" in run logs. */
  ownName: string;
  /** null while loading. */
  automations: Automation[] | null | 'failed';
  /** Which page of the area, from the address. */
  route: AutomationRoute;
  onNavigate: (url: string) => void;
  /** Reloads the list; the socket also reports changes by colleagues and the scheduler. */
  onChanged: () => void;
  /** Tests pin the week of the schedule. */
  now?: () => number;
}

type Dialog = { kind: 'edit'; automation: Automation | null } | { kind: 'take-over'; automation: Automation; input: AutomationInput | null } | { kind: 'delete'; automation: Automation };

/** Everything a page needs to link, act and report. */
interface PageContext {
  projectId: string;
  ownName: string;
  link: (route: AutomationRoute) => { href: string; onNavigate: (url: string) => void };
  actions: CardActions;
  notice: React.ReactNode;
}

/** The customer's automations (ADR 0016, 0023); every view, automation and run log has its own address. */
export function Automations({ projectId, ownName, automations, route, onNavigate, onChanged, now = Date.now }: AutomationsProps): React.JSX.Element {
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // A notice belongs to the page it was shown on.
  const address = automationUrl(projectId, route);
  useEffect(() => setNotice(null), [address]);

  const applied = (result: AutomationResult, success: string | null = null): void => {
    if (result.ok) {
      setDialog(null);
      if (success !== null) setNotice(success);
    } else {
      setNotice(result.error === 'tool-not-allowed' && result.detail !== null ? TEXT.toolNotAllowed(approvalTitle(result.detail)) : TEXT.saveFailed);
    }
    onChanged();
  };
  const failed = (): void => applied({ ok: false, error: 'failed', detail: null });

  const actions: CardActions = {
    setActive: (automation, active) =>
      void automationAction(projectId, automation.id, active ? 'resume' : 'pause').then(
        (result) => {
          if (!result.ok) setNotice(TEXT.pauseFailed);
          onChanged();
        },
        () => setNotice(TEXT.pauseFailed),
      ),
    run: (automation) =>
      void runAutomationNow(projectId, automation.id)
        .catch(() => false)
        .then((ok) => {
          setNotice(ok ? TEXT.runStarted(automation.name) : TEXT.runFailedToStart(automation.name));
          onChanged();
        }),
    edit: (automation) => setDialog({ kind: 'edit', automation }),
    remove: (automation) => setDialog({ kind: 'delete', automation }),
    takeOver: (automation) => setDialog({ kind: 'take-over', automation, input: null }),
    confirmTools: (automation) => void automationAction(projectId, automation.id, 'confirm-tools').then((result) => applied(result, TEXT.toolsConfirmed), failed),
  };

  const ctx: PageContext = {
    projectId,
    ownName,
    link: (target) => ({ href: automationUrl(projectId, target), onNavigate }),
    actions,
    notice:
      notice === null ? null : (
        <div className={styles.notice} role="status">
          <span>{notice}</span>
          <button type="button" className={styles.close} aria-label={TEXT.closeNotice} onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      ),
  };

  return (
    <>
      {route.view === 'run' ? (
        <RunPage ctx={ctx} automations={automations} id={route.id} runId={route.runId} />
      ) : route.view === 'automation' ? (
        <AutomationPage ctx={ctx} automations={automations} id={route.id} />
      ) : (
        <Overview ctx={ctx} automations={automations} view={route.view} now={now} onNew={() => setDialog({ kind: 'edit', automation: null })} />
      )}
      {dialog?.kind === 'edit' && (
        <Editor
          projectId={projectId}
          automation={dialog.automation}
          onCancel={() => setDialog(null)}
          onSave={async (input) => {
            const id = dialog.automation?.id ?? null;
            const result = await saveAutomation(projectId, id, input).catch((): AutomationResult => ({ ok: false, error: 'failed', detail: null }));
            if (!result.ok && result.error === 'owner-change' && dialog.automation !== null) setDialog({ kind: 'take-over', automation: dialog.automation, input });
            else applied(result);
          }}
        />
      )}
      {dialog?.kind === 'take-over' && (
        <Confirm
          title={TEXT.takeOverTitle}
          text={dialog.input === null ? TEXT.takeOverNoOwnerText(dialog.automation.name) : TEXT.takeOverText(dialog.automation.name, dialog.automation.owner?.name ?? null)}
          confirm={dialog.input === null ? TEXT.takeOver : TEXT.takeOverConfirm}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            const { automation, input } = dialog;
            const request = input === null ? automationAction(projectId, automation.id, 'take-over') : saveAutomation(projectId, automation.id, input, true);
            void request.then(applied, failed);
          }}
        />
      )}
      {dialog?.kind === 'delete' && (
        <Confirm
          title={TEXT.deleteTitle(dialog.automation.name)}
          text={TEXT.deleteText}
          confirm={TEXT.delete}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            const { automation } = dialog;
            void deleteAutomation(projectId, automation.id)
              .catch(() => false)
              .then((ok) => {
                setDialog(null);
                if (!ok) setNotice(TEXT.deleteFailed);
                // The automation's own pages no longer exist.
                else if (route.view === 'automation' || route.view === 'run') onNavigate(automationUrl(projectId, { view: 'list' }));
                onChanged();
              });
          }}
        />
      )}
    </>
  );
}

const TABS = ['list', 'schedule', 'runs'] as const;

interface OverviewProps {
  ctx: PageContext;
  automations: Automation[] | null | 'failed';
  view: (typeof TABS)[number];
  now: () => number;
  onNew: () => void;
}

/** Cards, weekly plan and all runs, as tabs with their own addresses. */
function Overview({ ctx, automations, view, now, onNew }: OverviewProps): React.JSX.Element {
  const attention = Array.isArray(automations) ? automations.filter((automation) => automation.attention).length : 0;
  return (
    <>
      <PageHeader
        {...sectionHeader.automations}
        actions={
          <button type="button" className={appStyles.headerButton} onClick={onNew}>
            {TEXT.newAutomation}
          </button>
        }
      />
      <main className={styles.main}>
        {ctx.notice}
        <nav aria-label={TEXT.automationViews} className={styles.tabs}>
          {TABS.map((item) => (
            <AppLink key={item} {...ctx.link({ view: item })} className={styles.tab} aria-current={view === item ? 'page' : undefined}>
              {tabText[item]}
            </AppLink>
          ))}
        </nav>
        {attention > 0 && <p className={styles.attention}>{TEXT.attentionNotice(attention)}</p>}
        {automations === null ? (
          <p className={styles.muted}>{TEXT.loading}</p>
        ) : automations === 'failed' ? (
          <p className={styles.muted}>{TEXT.loadFailed}</p>
        ) : view === 'runs' ? (
          <AllRuns ctx={ctx} automations={automations} />
        ) : automations.length === 0 ? (
          <p className={styles.empty}>{TEXT.noAutomations}</p>
        ) : view === 'schedule' ? (
          <WeekPlan automations={automations} now={new Date(now())} />
        ) : (
          <section className={styles.grid}>
            {automations.map((automation) => (
              <Card key={automation.id} ctx={ctx} automation={automation} />
            ))}
          </section>
        )}
      </main>
    </>
  );
}

interface CardActions {
  setActive: (automation: Automation, active: boolean) => void;
  run: (automation: Automation) => void;
  edit: (automation: Automation) => void;
  remove: (automation: Automation) => void;
  takeOver: (automation: Automation) => void;
  confirmTools: (automation: Automation) => void;
}

function statusOf(automation: Automation): { label: string; className: string | undefined } {
  if (automation.running) return { label: TEXT.running, className: styles.on };
  if (automation.paused !== null) return { label: pauseText(automation.paused, automation.pausedIntegration, automation.mine), className: styles.warn };
  if (!automation.active) return { label: TEXT.pausedBy(automation.pausedBy ?? '?'), className: styles.off };
  const last = automation.lastRun;
  if (automation.attention && last !== null) return { label: runStatusText[last.status], className: styles.bad };
  return { label: TEXT.active, className: styles.on };
}

function lastRunText(run: RunSummary | null): string {
  return run === null ? TEXT.neverRun : `${WHEN.format(run.startedAt)} · ${runStatusText[run.status]}`;
}

function ownerText(automation: Automation): string {
  if (automation.owner === null) return TEXT.noOwner;
  return automation.mine ? `${automation.owner.name} (${TEXT.you})` : automation.owner.name;
}

function Facts({ automation }: { automation: Automation }): React.JSX.Element {
  return (
    <dl className={styles.facts}>
      <dt>{TEXT.automationSchedule}</dt>
      <dd>{scheduleText(automation.schedule)}</dd>
      <dt>{TEXT.owner}</dt>
      <dd>{ownerText(automation)}</dd>
      <dt>{TEXT.allowedTools}</dt>
      <dd>{automation.allowedTools.length === 0 ? TEXT.noneAllowed : automation.allowedTools.map(approvalTitle).join(', ')}</dd>
      <dt>{TEXT.lastRun}</dt>
      <dd>{lastRunText(automation.lastRun)}</dd>
      {automation.nextRunAt !== null && (
        <>
          <dt>{TEXT.nextRun}</dt>
          <dd>{WHEN.format(automation.nextRunAt)}</dd>
        </>
      )}
    </dl>
  );
}

function Card({ ctx, automation }: { ctx: PageContext; automation: Automation }): React.JSX.Element {
  const status = statusOf(automation);
  return (
    <article className={styles.card} aria-label={automation.name}>
      <div className={styles.cardHead}>
        <div className={styles.cardTitle}>
          <h2 className={styles.name}>
            <AppLink {...ctx.link({ view: 'automation', id: automation.id })} className={styles.nameLink}>
              {automation.name}
            </AppLink>
          </h2>
          <span className={status.className}>{status.label}</span>
        </div>
        <PauseSwitch automation={automation} onChange={(active) => ctx.actions.setActive(automation, active)} />
      </div>
      <p className={styles.prompt}>{automation.prompt}</p>
      <Facts automation={automation} />
      <Actions ctx={ctx} automation={automation} withRuns />
    </article>
  );
}

/** Switches the scheduled runs on and off, as in the click demo; "run now" stays available. */
function PauseSwitch({ automation, onChange }: { automation: Automation; onChange: (active: boolean) => void }): React.JSX.Element {
  return (
    <button
      type="button"
      className={styles.switch}
      aria-pressed={automation.active}
      aria-label={TEXT.pauseSwitch(automation.name, automation.active)}
      onClick={() => onChange(!automation.active)}
    >
      <span className={styles.track}>
        <span className={styles.knob} />
      </span>
    </button>
  );
}

function Actions({ ctx, automation, withRuns }: { ctx: PageContext; automation: Automation; withRuns: boolean }): React.JSX.Element {
  const { actions } = ctx;
  return (
    <div className={styles.actions}>
      {automation.paused === 'no-owner' ? (
        <button type="button" className={styles.primary} onClick={() => actions.takeOver(automation)}>
          {TEXT.takeOver}
        </button>
      ) : automation.paused === 'tools-unconfirmed' && automation.mine ? (
        <button type="button" className={styles.primary} onClick={() => actions.confirmTools(automation)}>
          {TEXT.confirmTools}
        </button>
      ) : (
        <button type="button" className={styles.primary} disabled={automation.paused !== null} onClick={() => actions.run(automation)}>
          {TEXT.runNow}
        </button>
      )}
      <button type="button" className={styles.secondary} onClick={() => actions.edit(automation)}>
        {TEXT.edit}
      </button>
      {withRuns && (
        <AppLink {...ctx.link({ view: 'automation', id: automation.id })} className={styles.secondary}>
          {TEXT.details}
        </AppLink>
      )}
      {automation.mine && (
        <button type="button" className={styles.secondary} onClick={() => actions.remove(automation)}>
          {TEXT.delete}
        </button>
      )}
    </div>
  );
}

function duration(run: RunSummary): string {
  if (run.endedAt === null || run.endedAt === run.startedAt) return '–';
  const seconds = Math.max(1, Math.round((run.endedAt - run.startedAt) / 1000));
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

/** Loads again whenever `key` changes, e.g. the automations list after a run started or ended. */
function useLoaded<T>(load: () => Promise<T>, key: unknown): T | null | 'failed' {
  const [value, setValue] = useState<T | null | 'failed'>(null);
  useEffect(() => {
    let current = true;
    load().then(
      (loaded) => {
        if (current) setValue(loaded);
      },
      () => {
        if (current) setValue((previous) => (previous === null ? 'failed' : previous));
      },
    );
    return () => {
      current = false;
    };
    // `load` is a new function on every render; `key` alone says when to load again.
  }, [key]);
  return value;
}

/** Shown when an address names an automation or run that does not exist (any more). */
function Missing({ ctx, text }: { ctx: PageContext; text: string }): React.JSX.Element {
  return (
    <>
      <PageHeader {...sectionHeader.automations} />
      <main className={styles.main}>
        <p className={styles.empty}>{text}</p>
        <AppLink {...ctx.link({ view: 'list' })} className={styles.back}>
          ← {TEXT.backToAutomations}
        </AppLink>
      </main>
    </>
  );
}

function Loading(): React.JSX.Element {
  return (
    <>
      <PageHeader {...sectionHeader.automations} />
      <main className={styles.main}>
        <p className={styles.muted}>{TEXT.loading}</p>
      </main>
    </>
  );
}

function AutomationPage({ ctx, automations, id }: { ctx: PageContext; automations: Automation[] | null | 'failed'; id: string }): React.JSX.Element {
  const automation = Array.isArray(automations) ? automations.find((candidate) => candidate.id === id) : undefined;
  const runs = useLoaded(() => fetchRuns(ctx.projectId, id), automation);
  if (automations === null) return <Loading />;
  if (automation === undefined) return <Missing ctx={ctx} text={TEXT.automationMissing} />;
  const status = statusOf(automation);
  return (
    <>
      <PageHeader eyebrow={TEXT.automation} title={automation.name} subtitle={`${scheduleText(automation.schedule)} · ${TEXT.owner}: ${ownerText(automation)}`} />
      <main className={styles.main}>
        {ctx.notice}
        <AppLink {...ctx.link({ view: 'list' })} className={styles.back}>
          ← {TEXT.backToAutomations}
        </AppLink>
        <section className={styles.detail} aria-label={automation.name}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>
              <span className={status.className}>{status.label}</span>
            </div>
            <PauseSwitch automation={automation} onChange={(active) => ctx.actions.setActive(automation, active)} />
          </div>
          <h2 className={styles.label}>{TEXT.automationPrompt}</h2>
          <p className={styles.fullPrompt}>{automation.prompt}</p>
          <Facts automation={automation} />
          <Actions ctx={ctx} automation={automation} withRuns={false} />
        </section>
        <h2 className={styles.sectionTitle}>{TEXT.showRuns}</h2>
        <RunTable ctx={ctx} runs={runs} />
      </main>
    </>
  );
}

interface RunTableProps {
  ctx: PageContext;
  /** null while loading. */
  runs: RunSummary[] | null | 'failed';
  /** With an automation column for the runs of all automations. */
  automations?: Automation[];
}

function RunTable({ ctx, runs, automations }: RunTableProps): React.JSX.Element {
  if (runs === null) return <p className={styles.muted}>{TEXT.loading}</p>;
  if (runs === 'failed') return <p className={styles.muted}>{TEXT.loadFailed}</p>;
  if (runs.length === 0) return <p className={styles.muted}>{TEXT.noRuns}</p>;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {automations !== undefined && <th>{TEXT.automation}</th>}
            <th>{TEXT.runStart}</th>
            <th>{TEXT.runTrigger}</th>
            <th>{TEXT.runDuration}</th>
            <th>{TEXT.runStatus}</th>
            <th>{TEXT.owner}</th>
            <th>{TEXT.runCost}</th>
            <th>{TEXT.runFiles}</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const automation = automations?.find((candidate) => candidate.id === run.automationId);
            return (
              <tr key={run.id}>
                {automations !== undefined && (
                  <td>
                    {automation === undefined ? (
                      '–'
                    ) : (
                      <AppLink {...ctx.link({ view: 'automation', id: automation.id })} className={styles.link}>
                        {automation.name}
                      </AppLink>
                    )}
                  </td>
                )}
                <td>
                  <AppLink {...ctx.link({ view: 'run', id: run.automationId, runId: run.id })} className={styles.link} aria-label={`${TEXT.runLog}: ${WHEN.format(run.startedAt)}`}>
                    {WHEN.format(run.startedAt)}
                  </AppLink>
                </td>
                <td>{runTriggerText[run.trigger]}</td>
                <td>{duration(run)}</td>
                <td>
                  {runStatusText[run.status]}
                  {run.detail !== null && run.status !== 'ok' && <div className={styles.muted}>{run.detail}</div>}
                </td>
                <td>{run.ownerName ?? '–'}</td>
                <td>{run.costUsd === null ? '–' : COST.format(run.costUsd)}</td>
                <td>{run.files.length}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The latest runs of all automations; reloaded whenever the list changes, e.g. when a run starts or ends. */
function AllRuns({ ctx, automations }: { ctx: PageContext; automations: Automation[] }): React.JSX.Element {
  const runs = useLoaded(() => fetchAllRuns(ctx.projectId), automations);
  return <RunTable ctx={ctx} runs={runs} automations={automations} />;
}

interface RunPageProps {
  ctx: PageContext;
  automations: Automation[] | null | 'failed';
  id: string;
  runId: string;
}

/** One run log on its own page, laid out like the chat; a running run is reloaded until it ends. */
function RunPage({ ctx, automations, id, runId }: RunPageProps): React.JSX.Element {
  const [run, setRun] = useState<RunSummary | null | 'missing'>(null);
  const [events, setEvents] = useState<ServerEvent[] | null>(null);
  const automation = Array.isArray(automations) ? automations.find((candidate) => candidate.id === id) : undefined;
  const running = run !== null && run !== 'missing' && run.status === 'running';

  useEffect(() => {
    let current = true;
    const load = (): void => {
      Promise.all([fetchRun(ctx.projectId, id, runId), fetchRunEvents(ctx.projectId, id, runId)]).then(
        ([loadedRun, loadedEvents]) => {
          if (!current) return;
          setRun(loadedRun ?? 'missing');
          setEvents(loadedEvents);
        },
        () => {
          if (current) setRun((previous) => previous ?? 'missing');
        },
      );
    };
    load();
    const timer = running ? setInterval(load, RUN_POLL_MS) : null;
    return () => {
      current = false;
      if (timer !== null) clearInterval(timer);
    };
  }, [ctx.projectId, id, runId, running]);

  if (run === 'missing') return <Missing ctx={ctx} text={TEXT.runMissing} />;
  const name = automation?.name ?? TEXT.automation;
  const state = events === null || run === null ? null : reduce(initialState, { type: 'history', chatId: run.id, title: '', events });
  return (
    <>
      <PageHeader
        eyebrow={TEXT.runLog}
        title={name}
        subtitle={run === null ? TEXT.loading : `${WHEN.format(run.startedAt)} · ${runTriggerText[run.trigger]} · ${runStatusText[run.status]}`}
      />
      <main className={appStyles.main}>
        <div className={styles.runBar}>
          <AppLink {...ctx.link({ view: 'automation', id })} className={styles.back}>
            ← {name}
          </AppLink>
          {run !== null && (
            <dl className={styles.runFacts}>
              <dt>{TEXT.runStatus}</dt>
              <dd>{runStatusText[run.status]}</dd>
              <dt>{TEXT.runDuration}</dt>
              <dd>{duration(run)}</dd>
              <dt>{TEXT.owner}</dt>
              <dd>{run.ownerName ?? '–'}</dd>
              <dt>{TEXT.runCost}</dt>
              <dd>{run.costUsd === null ? '–' : COST.format(run.costUsd)}</dd>
            </dl>
          )}
          <p className={styles.muted}>{TEXT.runLogHint}</p>
        </div>
        {state === null ? (
          <p className={styles.runEmpty}>{TEXT.loading}</p>
        ) : state.items.length === 0 ? (
          <p className={styles.runEmpty}>{run === null ? '' : (run.detail ?? runStatusText[run.status])}</p>
        ) : (
          <Timeline state={state} projectId={ctx.projectId} ownName={ctx.ownName} send={() => undefined} />
        )}
      </main>
    </>
  );
}
